import moment, { Moment } from "moment";
import { TaskModel } from "../../Models/Models";
import styles from "../../Styles/calendarDashboard.module.css";
import { Vocabulary } from "../../Utils/Vocabulary";
import CalendarEventChip from "./CalendarEventChip";

const DAY_NAMES = [
  Vocabulary.calendarDayMon,
  Vocabulary.calendarDayTue,
  Vocabulary.calendarDayWed,
  Vocabulary.calendarDayThu,
  Vocabulary.calendarDayFri,
  Vocabulary.calendarDaySat,
  Vocabulary.calendarDaySun,
];

const MAX_CHIPS_PER_CELL = 3;

type Props = {
  currentDate: Moment;
  tasks: TaskModel[];
  onTaskClick: (task: TaskModel) => void;
  onDayClick: (dateStr: string) => void;
};

function getTaskDateKey(task: TaskModel): string | null {
  return task.started_at ?? task.created_at ?? null;
}

function buildMonthCells(date: Moment): Moment[] {
  const start = date.clone().startOf("month").startOf("isoWeek");
  const cells: Moment[] = [];
  for (let i = 0; i < 42; i++) {
    cells.push(start.clone().add(i, "days"));
  }
  return cells;
}

function groupTasksByDate(tasks: TaskModel[]): Record<string, TaskModel[]> {
  const map: Record<string, TaskModel[]> = {};
  tasks.forEach((task) => {
    const key = getTaskDateKey(task);
    if (!key) return;
    const dayKey = moment(key).format("YYYY-MM-DD");
    if (!map[dayKey]) map[dayKey] = [];
    map[dayKey].push(task);
  });
  return map;
}

export default function CalendarMonthView({ currentDate, tasks, onTaskClick, onDayClick }: Props) {
  const today = moment().format("YYYY-MM-DD");
  const currentMonth = currentDate.month();
  const cells = buildMonthCells(currentDate);
  const tasksByDate = groupTasksByDate(tasks);

  return (
    <div className={styles.calendarWrapper}>
      <div className={styles.dayHeader}>
        {DAY_NAMES.map((name) => (
          <div key={name} className={styles.dayHeaderCell}>
            {name}
          </div>
        ))}
      </div>

      <div className={styles.monthGrid}>
        {cells.map((cell) => {
          const dayKey = cell.format("YYYY-MM-DD");
          const isToday = dayKey === today;
          const isOtherMonth = cell.month() !== currentMonth;
          const dayTasks = tasksByDate[dayKey] ?? [];
          const visibleTasks = dayTasks.slice(0, MAX_CHIPS_PER_CELL);
          const hiddenCount = dayTasks.length - visibleTasks.length;

          const cellClass = [
            styles.dayCell,
            isOtherMonth ? styles.dayCellOtherMonth : "",
            isToday ? styles.dayCellToday : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <div
              key={dayKey}
              className={cellClass}
              onClick={() => onDayClick(dayKey)}
            >
              <span className={styles.dayNumber}>{cell.date()}</span>
              <div className={styles.chipRow}>
                {visibleTasks.map((task) => (
                  <CalendarEventChip
                    key={task.id}
                    task={task}
                    onClick={onTaskClick}
                    variant="month"
                  />
                ))}
                {hiddenCount > 0 && (
                  <span className={styles.moreLabel}>+{hiddenCount}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
